import { useEffect, useMemo } from "react";
import { useWatch, UseFormReturn } from "react-hook-form";

/**
 * Serve per collegare due Autocomplete RHF tra loro in modo bidirezionale.
 * Caso tipico:
 *  - Specialità  → filtra Prestazioni
 *  - Prestazione → imposta automaticamente Specialità corretta
 * Questo hook:
 * 1️. Filtra le options in base alla selezione dell’altro campo
 * 2️. Allinea automaticamente i valori quando necessario
 * 3️. Evita valori incoerenti nel form
 */

// Parametri generici per poter usare l'hook ovunque
type LinkedAutocompleteParams<TLeft, TRight> = {
  methods: UseFormReturn<any>;

  // Nome dei campi RHF (path completo nel form)
  // es: "prestazioniAbilitateForm.specialita"
  leftName: string;   
  rightName: string; 

  // Liste complete di opzioni (non filtrate)
  leftOptions: TLeft[];
  rightOptions: TRight[];

  // Funzioni per estrarre la chiave di confronto dalle option
  // Servono perché ogni dominio ha campi diversi (SPECIALITA, PRESTAZIONE, CODICE...)
  leftKey: (option: TLeft) => string;
  rightKey: (option: TRight) => string;

  // Funzione di "join": dato un elemento right, quale left gli appartiene?
  // Es: prestazione -> SPECIALITA
  rightToLeftKey: (right: TRight) => string;

  // Comportamenti opzionali
  autoSetLeftFromRight?: boolean;          
  clearInvalidRightOnLeftChange?: boolean; 
};

// Normalizzazione valori (evita problemi con null, spazi, numeri)
const norm = (v: any) => String(v ?? "").trim();

export function useLinkedAutocompleteOptions<TLeft, TRight>(
  p: LinkedAutocompleteParams<TLeft, TRight>,
) {
  const {
    methods,
    leftName,
    rightName,
    leftOptions,
    rightOptions,
    leftKey,
    rightKey,
    rightToLeftKey,
    autoSetLeftFromRight = true,
    clearInvalidRightOnLeftChange = true,
  } = p;

  /**
   * Uso useWatch per ottenere i valori attuali e che mi interessano
   * RHF ricaricherà questi valori solo quando cambiano
   */
  const leftValue = useWatch({ control: methods.control, name: leftName });
  const rightValue = useWatch({ control: methods.control, name: rightName });

  /**
   * FILTRO RIGHT OPTIONS
   * Se ho scelto una left (es: Specialità),
   * mostro solo le right (Prestazioni) compatibili
   * useMemo lo utilizzo per evitare refresh
   */

  const filteredRightOptions = useMemo(() => {
    if (!norm(leftValue)) return rightOptions;

    const left = norm(leftValue);

    return rightOptions.filter(
      (r) => norm(rightToLeftKey(r)) === left,
    );
  }, [leftValue, rightOptions, rightToLeftKey]);

  /**
   *  FILTRO LEFT OPTIONS (viceversa)
   *  Se ho scelto una Prestazione,
   *  mostro solo la Specialità coerente
   */
  const filteredLeftOptions = useMemo(() => {
    if (!norm(rightValue)) return leftOptions;

    const match = rightOptions.find(
      (r) => norm(rightKey(r)) === norm(rightValue),
    );

    if (!match) return leftOptions;

    const expectedLeft = norm(rightToLeftKey(match));
    if (!expectedLeft) return leftOptions;

    return leftOptions.filter(
      (l) => norm(leftKey(l)) === expectedLeft,
    );
  }, [rightValue, leftOptions, rightOptions, leftKey, rightKey, rightToLeftKey]);

  /**
   * AUTO-SET LEFT QUANDO CAMBIA RIGHT
   * Se seleziono una Prestazione,
   * imposto automaticamente la Specialità corretta
   * Controllo anche che il valore sia diverso,
   * per evitare loop di render
   */
  useEffect(() => {
    if (!autoSetLeftFromRight) return;
    if (!norm(rightValue)) return;

    const match = rightOptions.find(
      (r) => norm(rightKey(r)) === norm(rightValue),
    );

    const expectedLeft = match ? norm(rightToLeftKey(match)) : "";
    if (!expectedLeft) return;

    const currentLeft = methods.getValues(leftName);
    if (norm(currentLeft) === expectedLeft) return;

    methods.setValue(leftName, expectedLeft, {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
  }, [
    autoSetLeftFromRight,
    rightValue,
    rightOptions,
    rightKey,
    rightToLeftKey,
    methods,
    leftName,
  ]);

  /**
   * RESET RIGHT SE NON PIÙ VALIDA
   * Se cambio Specialità e la Prestazione selezionata
   * non appartiene più a quella Specialità,
   * la resetto
   * Si evitano così dati sbagliati nel formm
   */
  useEffect(() => {
    if (!clearInvalidRightOnLeftChange) return;
    if (!norm(leftValue) || !norm(rightValue)) return;

    const stillValid = filteredRightOptions.some(
      (r) => norm(rightKey(r)) === norm(rightValue),
    );

    if (stillValid) return;

    methods.setValue(rightName, "", {
      shouldDirty: true,
      shouldTouch: true,
      shouldValidate: true,
    });
  }, [
    clearInvalidRightOnLeftChange,
    leftValue,
    rightValue,
    filteredRightOptions,
    rightKey,
    methods,
    rightName,
  ]);

  /**
   * Faccio return delle options filtrate e valori correnti,
   * così il componente può usarli direttamente negli Autocomplete.
   */
  return {
    filteredLeftOptions,
    filteredRightOptions,
    leftValue,
    rightValue,
  };
}